/**
 * The `ic_portal` routing cookie. The marketing site (instacheckin.io) reads it to send the
 * Login button to the new console instead of the classic portal, so it is deliberately NOT
 * httpOnly and is set on a successful login or signup.
 *
 * The parent-domain scope comes from `VITE_PORTAL_COOKIE_DOMAIN`, which `infra/tool/deploy.sh`
 * derives from `rootDomain` in `infra/config/deploy-targets.json` and exports for PROD builds
 * only. Staging and dev builds never get it, so they can never write onto the prod parent
 * domain: they fall back to a host-only cookie.
 */
export const PORTAL_COOKIE_NAME = 'ic_portal';
export const PORTAL_COOKIE_VALUE = 'new';
export const PORTAL_COOKIE_MAX_AGE_SECONDS = 31_536_000;

export interface PortalCookieContext {
  readonly hostname: string;
  readonly protocol: string;
  /** Leading-dot parent domain, e.g. `.instacheckin.io`; blank/undefined = host-only. */
  readonly cookieDomain: string | undefined;
}

/** True when `hostname` is the configured domain itself or a subdomain of it. */
function isWithinDomain(hostname: string, cookieDomain: string): boolean {
  const bare = cookieDomain.replace(/^\./, '').toLowerCase();
  const host = hostname.toLowerCase();
  return host === bare || host.endsWith(`.${bare}`);
}

/** Builds the `document.cookie` assignment string. Pure, so it is unit-testable. */
export function buildPortalCookie({ hostname, protocol, cookieDomain }: PortalCookieContext): string {
  const parts = [
    `${PORTAL_COOKIE_NAME}=${PORTAL_COOKIE_VALUE}`,
    'Path=/',
    `Max-Age=${String(PORTAL_COOKIE_MAX_AGE_SECONDS)}`,
  ];
  if (cookieDomain !== undefined && cookieDomain !== '' && isWithinDomain(hostname, cookieDomain)) {
    parts.push(`Domain=${cookieDomain.startsWith('.') ? cookieDomain : `.${cookieDomain}`}`);
  }
  // Browsers drop Secure cookies set from plain http (local dev) on most engines.
  if (protocol === 'https:') parts.push('Secure');
  parts.push('SameSite=Lax');
  return parts.join('; ');
}

/** Marks this browser as a new-console user. Call after a successful login or signup. */
export function markPortalAsNew(): void {
  document.cookie = buildPortalCookie({
    hostname: window.location.hostname,
    protocol: window.location.protocol,
    cookieDomain: import.meta.env.VITE_PORTAL_COOKIE_DOMAIN,
  });
}
